/**
 * Plan Edit Page
 * Edit existing waste management plan using wizard
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { planService } from '../services/plan.service';
import { PlanEditor } from '../components/plan/PlanEditor';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import type { Plan, PlanData } from '../types';
import { toast } from '@/lib/utils/toast';

export function PlanEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [plan, setPlan] = useState<Plan | null>(null);
  const [planData, setPlanData] = useState<PlanData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadPlan = async () => {
      if (!id) return;

      try {
        const fetchedPlan = await planService.getPlan(id);
        setPlan(fetchedPlan);
        setPlanData(planService.parsePlanData(fetchedPlan));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load plan');
      } finally {
        setIsLoading(false);
      }
    };

    loadPlan();
  }, [id]);

  const handleSave = async (updates: Partial<PlanData>) => {
    if (!id) return;

    try {
      const updatedPlan = await planService.updatePlan(id, updates);

      if (updatedPlan) {
        // Success - show toast and navigate back
        toast.success('Plan updated successfully!');
        navigate(`/plans/${id}`);
      }
    } catch (err) {
    toast.error(err instanceof Error ? err.message : 'Failed to update plan');
    // Error handled here with toast, no need to re-throw
  }
  };

  const handleCancel = () => {
    navigate(`/plans/${id}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
          <p className="text-neutral-600">Loading plan...</p>
        </div>
      </div>
    );
  }

  if (error || !plan || !planData) {
    return (
      <div className="card text-center py-12">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg
            className="w-8 h-8 text-red-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-neutral-900 mb-2">Plan Not Found</h2>
        <p className="text-neutral-600 mb-6">{error || 'The requested plan could not be found'}</p>
        <button onClick={() => navigate('/')} className="btn-primary">
          Home
        </button>
      </div>
    );
  }

  return (
    <ErrorBoundary resetKeys={[id || '']}>
      <PlanEditor
        plan={plan}
        planData={planData}
        onSave={handleSave}
        onCancel={handleCancel}
      />
    </ErrorBoundary>
  );
}